import { useEffect, useRef, useState } from "react";
import { api, fmtPrice } from "@/lib/api";

const MAX_TICKS = 600;
const POLL_MS = 1500;
const PADDING = { top: 12, right: 70, bottom: 16, left: 8 };

function ticksToCandles(ticks, intervalMs) {
  if (ticks.length === 0) return [];
  const out = [];
  let cur = null;
  for (const tk of ticks) {
    const bucket = Math.floor(tk.t / intervalMs) * intervalMs;
    if (!cur || cur.t !== bucket) {
      if (cur) out.push(cur);
      cur = { t: bucket, open: tk.price, high: tk.price, low: tk.price, close: tk.price };
    } else {
      cur.high = Math.max(cur.high, tk.price);
      cur.low = Math.min(cur.low, tk.price);
      cur.close = tk.price;
    }
  }
  if (cur) out.push(cur);
  return out;
}

function seedTicks(currentPrice, symbol, count) {
  const vol = symbol === "USD/JPY" ? 0.012 :
              symbol === "XAU/USD" ? 0.06 : 0.00012;
  const now = Date.now();
  // Generate prices walking backward from current (prices[count-1] = current)
  const prices = new Array(count);
  prices[count - 1] = currentPrice;
  for (let i = count - 2; i >= 0; i--) {
    prices[i] = prices[i + 1] - (Math.random() - 0.5) * vol * 1.5;
  }
  // Emit ticks in time-ascending order (oldest first)
  const out = [];
  for (let i = 0; i < count; i++) {
    out.push({ t: now - (count - 1 - i) * POLL_MS, price: prices[i] });
  }
  return out;
}

function useElementSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setSize({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

function seedFromServer(ticksFromServer) {
  return ticksFromServer.map((t) => ({ t: t.t, price: t.price }));
}

export default function CandleChart({ symbol, height = 240, intervalMs = 10000 }) {
  const [ticks, setTicks] = useState([]);
  const [quote, setQuote] = useState(null);
  const seeded = useRef(false);
  const [containerRef, { w }] = useElementSize();

  useEffect(() => {
    let mounted = true;
    seeded.current = false;
    setTicks([]);

    const load = async () => {
      try {
        if (!seeded.current) {
          // Bootstrap from server tick history for continuity across reloads.
          const [{ data: hist }, { data: q }] = await Promise.all([
            api.get(`/market/history/${encodeURIComponent(symbol)}`, { params: { limit: 400 } }),
            api.get(`/market/rates/${encodeURIComponent(symbol)}`),
          ]);
          if (!mounted) return;
          seeded.current = true;
          setQuote(q);
          const serverTicks = seedFromServer(hist.ticks || []);
          serverTicks.push({ t: Date.now(), price: q.mid });
          setTicks(serverTicks);
        } else {
          const { data } = await api.get(`/market/rates/${encodeURIComponent(symbol)}`);
          if (!mounted) return;
          setQuote(data);
          setTicks((prev) => {
            const next = [...prev, { t: Date.now(), price: data.mid }];
            if (next.length > MAX_TICKS) return next.slice(next.length - MAX_TICKS);
            return next;
          });
        }
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { mounted = false; clearInterval(id); };
  }, [symbol]);

  const candles = ticksToCandles(ticks, intervalMs);

  if (candles.length === 0 || w === 0) {
    return (
      <div ref={containerRef} className="border border-subtle bg-card flex items-center justify-center text-xs text-muted-foreground" style={{ height: height + 60 }} data-testid={`candle-chart-${symbol.replace("/", "-")}`}>
        {candles.length === 0 ? "Loading chart…" : ""}
      </div>
    );
  }

  // Compute Y domain
  const highs = candles.map((c) => c.high);
  const lows = candles.map((c) => c.low);
  let yMin = Math.min(...lows);
  let yMax = Math.max(...highs);
  const pad = (yMax - yMin) * 0.18 || yMax * 0.0005;
  yMin -= pad;
  yMax += pad;

  // Chart inner area
  const plotW = Math.max(1, w - PADDING.left - PADDING.right);
  const plotH = Math.max(1, height - PADDING.top - PADDING.bottom);

  // Scales
  const xMin = candles[0].t;
  const xMax = candles[candles.length - 1].t;
  const xRange = Math.max(1, xMax - xMin);
  const xScale = (t) => PADDING.left + ((t - xMin) / xRange) * plotW;
  const yScale = (p) => PADDING.top + (1 - (p - yMin) / (yMax - yMin)) * plotH;

  // Candle width based on spacing
  let candleWidth = 6;
  if (candles.length > 1) {
    const spacing = plotW / candles.length;
    candleWidth = Math.max(2, Math.min(22, spacing * 0.65));
  }

  // Y-axis tick values (5 ticks)
  const tickCount = 5;
  const ticksY = [];
  for (let i = 0; i < tickCount; i++) {
    const v = yMin + ((yMax - yMin) * i) / (tickCount - 1);
    ticksY.push(v);
  }

  const up = quote ? quote.change >= 0 : true;
  const refY = quote ? yScale(quote.mid) : null;

  return (
    <div className="bg-card border border-subtle" data-testid={`candle-chart-${symbol.replace("/", "-")}`}>
      {quote && (
        <div className="px-4 py-3 border-b border-subtle flex items-center justify-between">
          <div>
            <div className="label-eyebrow">{symbol}</div>
            <div className="font-mono-num text-2xl font-semibold mt-0.5">{fmtPrice(symbol, quote.mid)}</div>
          </div>
          <div className="text-right">
            <div className={`font-mono-num text-sm font-semibold ${up ? "text-profit" : "text-loss"}`}>
              {up ? "+" : ""}{quote.change_pct.toFixed(3)}%
            </div>
            <div className="text-xs text-muted-foreground font-mono-num mt-0.5">
              {up ? "▲" : "▼"} {fmtPrice(symbol, Math.abs(quote.change))}
            </div>
          </div>
        </div>
      )}
      <div ref={containerRef} style={{ width: "100%", height }}>
        <svg width={w} height={height} style={{ display: "block" }}>
          {/* Y-axis grid + labels */}
          {ticksY.map((v, i) => {
            const y = yScale(v);
            return (
              <g key={i}>
                <line
                  x1={PADDING.left}
                  x2={PADDING.left + plotW}
                  y1={y}
                  y2={y}
                  stroke="#27272a"
                  strokeOpacity={0.45}
                  strokeDasharray="2 4"
                />
                <text
                  x={PADDING.left + plotW + 6}
                  y={y + 3}
                  fill="#a1a1aa"
                  fontSize="10"
                  fontFamily="IBM Plex Mono, ui-monospace, monospace"
                >
                  {fmtPrice(symbol, v)}
                </text>
              </g>
            );
          })}

          {/* Current price reference line */}
          {refY !== null && (
            <line
              x1={PADDING.left}
              x2={PADDING.left + plotW}
              y1={refY}
              y2={refY}
              stroke={up ? "#16a34a" : "#dc2626"}
              strokeOpacity={0.55}
              strokeDasharray="3 4"
            />
          )}

          {/* Candles */}
          {candles.map((c, i) => {
            const x = xScale(c.t);
            const yOpen = yScale(c.open);
            const yClose = yScale(c.close);
            const yHigh = yScale(c.high);
            const yLow = yScale(c.low);
            const candleUp = c.close >= c.open;
            const color = candleUp ? "#16a34a" : "#dc2626";
            const bodyTop = Math.min(yOpen, yClose);
            const bodyHeight = Math.max(1, Math.abs(yClose - yOpen));
            return (
              <g key={i}>
                <line x1={x} x2={x} y1={yHigh} y2={yLow} stroke={color} strokeWidth={1} />
                <rect
                  x={x - candleWidth / 2}
                  y={bodyTop}
                  width={candleWidth}
                  height={bodyHeight}
                  fill={color}
                  stroke={color}
                />
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
